import { useIsWorkInSessionEnabled } from '@/app-shell/flags/agents-flags';
import {
  pendingChatMessageAtom,
  pendingChipMetaAtom,
  pendingEntryMethodAtom,
} from '@/atoms/chatLaunchAtoms';
import {
  activeSidebarChatIdAtom,
  sidebarChatIdAtomFamily,
} from '@/atoms/chatSidebarAtoms';
import { pendingWorkFirstTurnAtom } from '@heidi/agents/data';
import type {
  ChipOrigin,
  ChipSurface,
  MessageEntryMethod,
} from '@heidi/evidence/contracts';
import { useQuerySessionById } from '@heidi/session/data';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

export interface AskHeidiChipMeta {
  label: string;
  category: string;
  origin?: ChipOrigin;
  surface?: ChipSurface;
}

export interface StageAskHeidiQueryArgs {
  message: string;
  /** Analytics entry method — omitted for non-chip sends (e.g. slash protocol). */
  entryMethod?: MessageEntryMethod;
  /** Chip metadata for the Evidence pending hand-off — omitted for non-chip sends. */
  chipMeta?: AskHeidiChipMeta;
}

/**
 * Single chokepoint for staging an Ask Heidi query fired from a chip / prompt
 * (as opposed to the free-text composer, which lives in ``useFloatingAskHeidi``).
 *
 * Inside a session with Work-in-session enabled, the RHS panel is the Work
 * panel — which never reads the Evidence ``pendingChatMessage`` hand-off — so
 * routing a chip through the Evidence path would just open an unrelated panel
 * and drop the query. Here we instead stash a ``pendingWorkFirstTurn`` so
 * ``SessionWorkPanel`` spins up a fresh project seeded with the chip's query
 * (Evidence's "always new" rule). Everywhere else it's the unchanged Evidence
 * hand-off: clear the active thread (DD-011) and stash the message for
 * ``ChatInput`` to auto-send when the panel mounts.
 *
 * @param sessionId current session, or ``undefined`` outside a session route
 * @param openPanel opens whichever sidebar panel this surface targets
 */
export function useStageAskHeidiQuery(
  sessionId: string | undefined,
  openPanel: () => void
): (args: StageAskHeidiQueryArgs) => void {
  const setPendingMessage = useSetAtom(pendingChatMessageAtom);
  const setPendingEntryMethod = useSetAtom(pendingEntryMethodAtom);
  const setPendingChipMeta = useSetAtom(pendingChipMetaAtom);
  const setActiveSidebarChatId = useSetAtom(activeSidebarChatIdAtom);
  const setSidebarChatId = useSetAtom(sidebarChatIdAtomFamily(sessionId ?? ''));

  const isWorkInSessionEnabled = useIsWorkInSessionEnabled();
  const setPendingWorkFirstTurn = useSetAtom(pendingWorkFirstTurnAtom);
  // `?? ''` mirrors the composer's `useCurrentSessionId()` (which returns '' off
  // a session route): the query's `enabled: !!sessionId` gate no-ops it, and a
  // `string` (not `string | undefined`) lets `select` infer T as the session
  // name rather than falling back to the default `QuerySessionResponse`.
  const { data: sessionName } = useQuerySessionById({
    sessionId: sessionId ?? '',
    select: (data) => data?.session_name,
  });

  return useCallback(
    ({ message, entryMethod, chipMeta }: StageAskHeidiQueryArgs) => {
      const routeToWork = isWorkInSessionEnabled && !!sessionId;

      if (routeToWork) {
        setPendingWorkFirstTurn({
          sessionId,
          prompt: message,
          linked: {
            type: 'session',
            sessionId,
            sessionName: sessionName?.trim() || 'Untitled session',
          },
        });
        openPanel();
        return;
      }

      setActiveSidebarChatId(null);
      setSidebarChatId(null);
      setPendingMessage(message);
      if (entryMethod) setPendingEntryMethod(entryMethod);
      if (chipMeta) setPendingChipMeta(chipMeta);
      openPanel();
    },
    [
      isWorkInSessionEnabled,
      openPanel,
      sessionId,
      sessionName,
      setActiveSidebarChatId,
      setPendingChipMeta,
      setPendingEntryMethod,
      setPendingMessage,
      setPendingWorkFirstTurn,
      setSidebarChatId,
    ]
  );
}
